/**
 * App i18n locale codes <-> backend / preview-site language codes.
 *
 * The app's i18n FOLDER codes are chosen to be Intl-safe so pluralization and the i18n
 * extract/check gate behave: Brazil is `bra`, Argentina is `arg`, because `br`/`ar` are read
 * by CLDR as Breton / Arabic. The BACKEND catalogue and the framed preview site use the
 * country-code scheme (`br`, `ar`), where `ar` ALWAYS means Argentina (Arabic, if ever added,
 * would take its own country's code). South Africa is the exception the other way: the backend
 * and the preview iframe use the LANGUAGE code `af` (not the country code `za`), so the app's
 * `za` maps OUT to `af`. These helpers convert at the API / preview boundary.
 *
 * Rule: inside the app everything uses APP codes (i18next, the store, comparisons). Convert to
 * a backend code only when SENDING out, that is saving preferences, the preview `&lang=`, and
 * reading backend multi-language data; convert an inbound backend code to an app code at
 * ingress (startProperties, a loaded preference) before it reaches i18next.
 *
 * Codes not listed pass through unchanged: they already match (`mx`/`cl`/`co`/`pe`/`en`)
 * or have no backend entry yet (`ve`/`cfr`/`cen`).
 */
const APP_TO_BACKEND: Readonly<Record<string, string>> = { bra: 'br', arg: 'ar', za: 'af' };
const BACKEND_TO_APP: Readonly<Record<string, string>> = { br: 'bra', ar: 'arg', af: 'za' };

/** App i18n code -> backend / preview-site language code (egress: save, preview, data reads). */
export const toBackendLang = (code: string): string => APP_TO_BACKEND[code] ?? code;

/** Backend / preview-site language code -> app i18n code (ingress: startProperties, saved pref). */
export const toAppLang = (code: string): string => BACKEND_TO_APP[code] ?? code;

/**
 * App i18n code -> whitelabels.com PUBLIC-SITE locale segment (e.g. the legal-page links).
 *
 * The public site uses the country-code scheme like {@link toBackendLang} (`bra`->`br`,
 * `arg`->`ar`), with ONE documented exception: South Africa is served at `/za` (the country code),
 * NOT the backend/preview language code `af`. So this is `toBackendLang` with `za` held back — the
 * single home for that public-site inconsistency. `en` and already-matching codes pass through.
 */
export const toWebsiteLang = (code: string): string => (code === 'za' ? 'za' : toBackendLang(code));
